/**
 * Which body a member profile renders (specs/competitions-hub-client AC-19.1).
 *
 * The profile is opened from a squad leaderboard row in ANY competition, and the viewer's own
 * row used to be special-cased into the World Cup's editable `PicksView`. That component reaches
 * `useWorldCupPicksData()`, which takes no competition argument and hardcodes
 * `WORLD_CUP_COMPETITION_ID` for its matches, futures, golden ball and every cache key — so
 * tapping yourself inside a Champions League squad showed your **World Cup** picks and futures,
 * while tapping anyone else showed the right competition.
 *
 * The editable feed is therefore only ever correct on the World Cup. Everywhere else the viewer
 * gets the same competition-scoped profile body their squadmates see: the server already returns
 * a viewer's own futures unhidden (`futuresHidden = target !== viewer && !revealed`), so self
 * loses nothing but the inline editing, which lives on the competition's own Picks tab.
 */

import { WORLD_CUP_COMPETITION_ID } from '../../worldcup/worldcup.constants';

export type MemberProfileBody =
  /** The World Cup's editable picks feed, with the profile header bolted on top. */
  | 'worldcup-editable-feed'
  /** The competition-scoped read-only profile: header, stats, futures, match cards. */
  | 'competition-profile';

export interface MemberProfileBodyInput {
  /** The `self=1` query param. Spoofable on its own — never trusted without the id check. */
  isSelfParam: boolean;
  /** The signed-in user, when auth has resolved. */
  viewerUserId: string | null | undefined;
  /** The profile being viewed. */
  memberId: string;
  competitionId: string;
}

export interface MemberProfileBodyResolution {
  /**
   * Whether this really is the viewer's own profile — the `self` param AND a matching id.
   * The editable futures pull the *viewer's* picks, so a mismatched id would render your
   * editor under someone else's header.
   */
  isOwnProfile: boolean;
  body: MemberProfileBody;
}

export function resolveMemberProfileBody(
  input: MemberProfileBodyInput,
): MemberProfileBodyResolution {
  const { isSelfParam, viewerUserId, memberId, competitionId } = input;

  const isOwnProfile = isSelfParam
    && viewerUserId != null
    && viewerUserId !== ''
    && memberId === viewerUserId;

  const body: MemberProfileBody = isOwnProfile && competitionId === WORLD_CUP_COMPETITION_ID
    ? 'worldcup-editable-feed'
    : 'competition-profile';

  return { isOwnProfile, body };
}
